<!-- views/adgroups/index.vue -->
<template>
  <div class="adgroup-list">
    <!-- 页面标题 -->
    <div class="page-header">
      <div class="header-left">
        <h2>广告组管理</h2>
        <el-breadcrumb separator="/" v-if="routeQueryCampaignName">
          <el-breadcrumb-item :to="{ path: '/campaigns' }">广告系列管理</el-breadcrumb-item>
          <el-breadcrumb-item>{{ routeQueryCampaignName }}</el-breadcrumb-item>
        </el-breadcrumb>
      </div>
      <div class="header-actions">
        <field-selector
            v-model="selectedFields"
            :fields="allFields"
            :default-fields="defaultFields"
            @confirm="handleFieldsConfirm"
        />
        <el-button type="primary" icon="el-icon-plus" @click="handleCreate">
          创建广告组
        </el-button>
      </div>
    </div>

    <!-- 筛选栏 -->
    <el-card shadow="never" class="filter-card">
      <el-form :model="filter" inline>
        <el-form-item label="广告组名称">
          <el-input
              v-model="filter.adgroup_name"
              placeholder="请输入广告组名称"
              clearable
              style="width: 200px"
          />
        </el-form-item>

        <el-form-item label="所属系列" v-if="!routeQueryCampaignId">
          <el-select
              v-model="filter.campaign_id"
              placeholder="请选择"
              clearable
              filterable
              style="width: 200px"
              @change="handleCampaignChange"
          >
            <el-option
                v-for="item in campaignOptions"
                :key="item.campaign_id"
                :label="item.campaign_name"
                :value="item.campaign_id"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="优化目标">
          <el-select
              v-model="filter.optimization_goal"
              placeholder="全部"
              clearable
              style="width: 140px"
          >
            <el-option
                v-for="item in optimizationGoalOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="推广类型">
          <el-select
              v-model="filter.promotion_type"
              placeholder="全部"
              clearable
              style="width: 140px"
          >
            <el-option label="Android应用" value="APP_ANDROID" />
            <el-option label="iOS应用" value="APP_IOS" />
            <el-option label="网站" value="WEBSITE" />
            <el-option label="销售线索" value="LEAD_GENERATION" />
          </el-select>
        </el-form-item>

        <el-form-item label="状态">
          <el-select
              v-model="filter.operation_status"
              placeholder="全部"
              clearable
              style="width: 100px"
          >
            <el-option label="启用" value="ENABLE" />
            <el-option label="暂停" value="DISABLE" />
          </el-select>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 数据表格 -->
    <el-card shadow="never">
      <el-table
          :data="adgroups"
          v-loading="loading"
          stripe
          style="width: 100%"
          :key="tableKey"
      >
        <!-- 动态列 -->
        <template v-for="field in displayFields">
          <!-- ID 列 -->
          <el-table-column
              v-if="field.key === 'adgroup_id'"
              :key="field.key"
              :prop="field.key"
              :label="field.label"
              :width="field.width || 180"
          >
            <template slot-scope="{ row }">
              <div class="id-cell">
                <el-tooltip :content="row[field.key]" placement="top">
                  <span class="id-text">{{ row[field.key] | truncate(16) }}</span>
                </el-tooltip>
                <i
                    class="el-icon-document-copy copy-icon"
                    @click="copyToClipboard(row[field.key])"
                ></i>
              </div>
            </template>
          </el-table-column>

          <!-- 广告组名称列 -->
          <el-table-column
              v-else-if="field.key === 'adgroup_name'"
              :key="field.key"
              :prop="field.key"
              :label="field.label"
              :min-width="field.minWidth || 180"
          >
            <template slot-scope="{ row }">
              <span class="adgroup-name">{{ row[field.key] }}</span>
            </template>
          </el-table-column>

          <!-- 预算列 -->
          <el-table-column
              v-else-if="field.key === 'budget'"
              :key="field.key"
              :label="field.label"
              :width="field.width || 150"
          >
            <template slot-scope="{ row }">
              <div v-if="row.budget_mode === 'BUDGET_MODE_INFINITE'">
                无限预算
              </div>
              <div v-else>
                <div>¥{{ formatBudget(row.budget) }}</div>
                <div class="budget-mode">{{ getBudgetModeLabel(row.budget_mode) }}</div>
              </div>
            </template>
          </el-table-column>

          <!-- 出价方式列 -->
          <el-table-column
              v-else-if="field.key === 'bid_type'"
              :key="field.key"
              :label="field.label"
              :width="field.width || 100"
          >
            <template slot-scope="{ row }">
              {{ getBidTypeLabel(row.bid_type) }}
              <span v-if="row.conversion_bid_price" class="bid-price">
                (¥{{ (row.conversion_bid_price / 100).toFixed(2) }})
              </span>
            </template>
          </el-table-column>

          <!-- 投放位置列 -->
          <el-table-column
              v-else-if="field.key === 'placements'"
              :key="field.key"
              :label="field.label"
              :width="field.width || 150"
          >
            <template slot-scope="{ row }">
              <template v-if="row.placements && row.placements.length">
                <el-tag
                    v-for="placement in row.placements"
                    :key="placement"
                    size="small"
                    style="margin-right: 4px; margin-bottom: 2px;"
                >
                  {{ getPlacementLabel(placement) }}
                </el-tag>
              </template>
              <span v-else-if="row.placement_type === 'PLACEMENT_TYPE_AUTOMATIC'">
                自动投放
              </span>
              <span v-else>-</span>
            </template>
          </el-table-column>

          <!-- 状态列 -->
          <el-table-column
              v-else-if="field.key === 'operation_status'"
              :key="field.key"
              :prop="field.key"
              :label="field.label"
              :width="field.width || 100"
          >
            <template slot-scope="{ row }">
              <status-tag :status="row.operation_status" />
            </template>
          </el-table-column>

          <!-- 优化目标列 -->
          <el-table-column
              v-else-if="field.key === 'optimization_goal'"
              :key="field.key"
              :label="field.label"
              :width="field.width || 120"
          >
            <template slot-scope="{ row }">
              {{ getOptimizationGoalLabel(row.optimization_goal) }}
            </template>
          </el-table-column>

          <!-- 推广类型列 -->
          <el-table-column
              v-else-if="field.key === 'promotion_type'"
              :key="field.key"
              :label="field.label"
              :width="field.width || 120"
          >
            <template slot-scope="{ row }">
              {{ getPromotionTypeLabel(row.promotion_type) }}
            </template>
          </el-table-column>

          <!-- 定向摘要列 -->
          <el-table-column
              v-else-if="field.key === 'targeting_summary'"
              :key="field.key"
              :label="field.label"
              :width="field.width || 200"
              :show-overflow-tooltip="true"
          >
            <template slot-scope="{ row }">
              <span class="targeting-summary">{{ getTargetingSummary(row) }}</span>
            </template>
          </el-table-column>

          <!-- 时间列 -->
          <el-table-column
              v-else-if="field.key === 'create_time' || field.key === 'modify_time'"
              :key="field.key"
              :prop="field.key"
              :label="field.label"
              :width="field.width || 160"
          >
            <template slot-scope="{ row }">
              <div class="time-text">{{ formatTime(row[field.key]) }}</div>
            </template>
          </el-table-column>

          <!-- 普通文本列 -->
          <el-table-column
              v-else
              :key="field.key"
              :prop="field.key"
              :label="field.label"
              :width="field.width"
              :min-width="field.minWidth || 120"
              :show-overflow-tooltip="true"
          >
            <template slot-scope="{ row }">
              {{ formatFieldValue(row[field.key]) }}
            </template>
          </el-table-column>
        </template>

        <!-- 操作列 -->
        <el-table-column label="操作" width="260" >
          <template slot-scope="{ row }">
            <el-button
                type="text"
                size="small"
                @click="handleEdit(row)"
            >
              编辑
            </el-button>
            <el-button
                type="text"
                size="small"
                @click="handleBudgetEdit(row)"
            >
              调整预算
            </el-button>
            <el-button
                v-if="row.operation_status === 'ENABLE'"
                type="text"
                size="small"
                @click="handlePause(row)"
            >
              暂停
            </el-button>
            <el-button
                v-else-if="row.operation_status === 'DISABLE'"
                type="text"
                size="small"
                @click="handleResume(row)"
            >
              启用
            </el-button>
            <el-button
                type="text"
                size="small"
                style="color: #f56c6c"
                @click="handleDelete(row)"
            >
              删除
            </el-button>
            <el-button
                type="text"
                size="small"
                @click="handleViewAds(row)"
            >
              查看广告
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
            background
            :current-page="pagination.page"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 创建/编辑弹窗 -->
    <el-dialog
        :title="dialogTitle"
        :visible.sync="dialogVisible"
        width="600px"
        @close="handleDialogClose"
        class="adgroup-dialog"
    >
      <el-form
          ref="adgroupForm"
          :model="formData"
          :rules="formRules"
          label-width="120px"
      >
        <!-- 基础信息 -->
        <el-divider content-position="left">基础信息</el-divider>

        <el-form-item label="广告组名称" prop="adgroup_name">
          <el-input v-model="formData.adgroup_name" placeholder="请输入广告组名称" />
        </el-form-item>

        <el-form-item label="所属系列" prop="campaign_id" v-if="!routeQueryCampaignId">
          <el-select v-model="formData.campaign_id" placeholder="请选择" style="width: 100%">
            <el-option
                v-for="item in campaignOptions"
                :key="item.campaign_id"
                :label="item.campaign_name"
                :value="item.campaign_id"
            />
          </el-select>
        </el-form-item>

        <!-- 推广配置 -->
        <el-divider content-position="left">推广配置</el-divider>

        <el-form-item label="推广类型" prop="promotion_type">
          <el-select v-model="formData.promotion_type" placeholder="请选择" style="width: 100%">

            <el-option label="小游戏" value="MINI_GAME" />

          </el-select>
        </el-form-item>
        <el-form-item label="小游戏/小程序" prop="minis_id" v-if="formData.promotion_type === 'MINI_GAME' || formData.promotion_type === 'MINI_APP'">
          <el-select
              v-model="formData.minis_id"
              placeholder="请选择小游戏/小程序"
              filterable              style="width: 100%"
          >
            <el-option
                v-for="item in minisOptions"
                :key="item.minis_id"
                :label="item.minis_name"
                :value="item.minis_id"
            />
          </el-select>
          <el-button
              type="text"
              size="small"
              @click="loadMinisOptions"
              :loading="minisLoading"
          >
            刷新列表
          </el-button>
        </el-form-item>
        <el-form-item label="优化目标" prop="optimization_goal">
          <el-select v-model="formData.optimization_goal" placeholder="请选择" style="width: 100%">
            <el-option
                v-for="item in optimizationGoalOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item
            label="转化事件"
            v-if="formData.optimization_goal === 'CONVERT' || formData.optimization_goal === 'IN_APP_EVENT'"
            prop="optimization_event"
        >
          <el-input v-model="formData.optimization_event" placeholder="请输入转化事件名称" />
          <span class="form-tip">如：PURCHASE、ADD_TO_CART、REGISTRATION</span>
        </el-form-item>

        <!-- 出价与预算 -->
        <el-divider content-position="left">出价与预算</el-divider>

        <el-form-item label="出价策略" prop="bid_type">
          <el-radio-group v-model="formData.bid_type">
            <el-radio label="BID_TYPE_NO_BID">最大转化（系统自动出价）</el-radio>
            <el-radio label="BID_TYPE_CUSTOM">成本控制（手动出价）</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item
            v-if="formData.bid_type === 'BID_TYPE_CUSTOM'"
            label="目标出价"
            prop="conversion_bid_price"
        >
          <el-input-number
              v-model="formData.conversion_bid_price"
              :min="100"
              :step="50"
              placeholder="单位：分"
          />
          <span class="form-tip">分/转化，¥{{ ((formData.conversion_bid_price || 0) / 100).toFixed(2) }}元</span>
        </el-form-item>
        <el-form-item
            v-if="formData.bid_type === 'BID_TYPE_CUSTOM'"
            label="目标平均点击成本"
            prop="conversion_bid_price"
        >
          <el-input-number
              v-model="formData.bid_price"
              :min="100"
              :step="50"
              placeholder="单位：分"
          />
          <span class="form-tip">分/转化，¥{{ ((formData.bid_price || 0) / 100).toFixed(2) }}元</span>
        </el-form-item>
        <!-- 价值优化 (VBO) -->
        <template v-if="formData.optimization_goal === 'VALUE'">
          <el-form-item label="深度出价策略" prop="deep_bid_type">
            <el-select v-model="formData.deep_bid_type" placeholder="请选择" style="width: 200px">
              <el-option label="最小ROAS" value="VO_MIN_ROAS" />
              <el-option label="最高价值" value="VO_HIGHEST_VALUE" />
            </el-select>
          </el-form-item>

          <el-form-item v-if="formData.deep_bid_type === 'VO_MIN_ROAS'" label="目标ROAS" prop="roas_bid">
            <el-input-number
                v-model="formData.roas_bid"
                :min="0.01"
                :max="1000"
                :step="0.01"
                :precision="2"
                placeholder="0.01-1000"
            />
            <span class="form-tip">例如：1.5 表示期望 150% 的回报</span>
          </el-form-item>
        </template>

        <!-- 投放排期 -->
        <el-divider content-position="left">投放排期</el-divider>

        <el-form-item label="排期类型" prop="schedule_type">
          <el-radio-group v-model="formData.schedule_type">
            <el-radio label="SCHEDULE_FROM_NOW">立即开始，长期投放</el-radio>
            <el-radio label="SCHEDULE_START_END">指定开始和结束时间</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="开始时间" prop="schedule_start_time">
          <el-date-picker
              v-model="formData.schedule_start_time"
              type="datetime"
              placeholder="选择开始时间"
              value-format="yyyy-MM-dd HH:mm:ss"
              :picker-options="startTimeOptions"
          />
        </el-form-item>

        <el-form-item
            v-if="formData.schedule_type === 'SCHEDULE_START_END'"
            label="结束时间"
            prop="schedule_end_time"
        >
          <el-date-picker
              v-model="formData.schedule_end_time"
              type="datetime"
              placeholder="选择结束时间"
              value-format="yyyy-MM-dd HH:mm:ss"
              :picker-options="endTimeOptions"
          />
        </el-form-item>

        <!-- 投放位置 -->
        <el-divider content-position="left">投放位置</el-divider>

        <el-form-item label="投放策略" prop="placement_type">
          <el-radio-group v-model="formData.placement_type" @change="handlePlacementTypeChange">
            <el-radio label="PLACEMENT_TYPE_AUTOMATIC">自动投放</el-radio>
            <el-radio label="PLACEMENT_TYPE_NORMAL">手动选择</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item
            v-if="formData.placement_type === 'PLACEMENT_TYPE_NORMAL'"
            label="选择位置"
            prop="placements"
        >
          <el-checkbox-group v-model="formData.placements">
            <el-checkbox label="PLACEMENT_TIKTOK">TikTok</el-checkbox>
            <el-checkbox label="PLACEMENT_PANGLE">Pangle</el-checkbox>
            <el-checkbox label="PLACEMENT_GLOBAL_APP_BUNDLE">Global App Bundle</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <!-- 定向设置 -->
        <el-divider content-position="left">定向设置</el-divider>

        <!-- 地理位置 -->
        <el-form-item label="地理位置">
          <div class="location-selector">
            <el-select
                v-model="targetingSpec.location_ids"
                multiple
                filterable
                collapse-tags
                placeholder="请选择地理位置"                style="width: 100%"
            >
              <el-option
                  v-for="item in locationOptions"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
              />
            </el-select>

          </div>
        </el-form-item>

        <!-- 语言 -->
        <el-form-item label="语言">
          <el-select
              v-model="targetingSpec.languages"
              multiple
              placeholder="请选择语言"
              style="width: 100%"
          >
            <el-option label="阿拉伯语" value="ar" />
            <el-option label="阿萨姆语" value="as" />
            <el-option label="哈里亚纳语" value="bgc" />
            <el-option label="比哈尔语" value="bh" />
            <el-option label="孟加拉语" value="bn" />
            <el-option label="捷克语" value="cs" />
            <el-option label="德语" value="de" />
            <el-option label="希腊语" value="el" />
            <el-option label="英语" value="en" />
            <el-option label="西班牙语" value="es" />
            <el-option label="芬兰语" value="fi" />
            <el-option label="法语" value="fr" />
            <el-option label="古吉拉特语" value="gu" />
            <el-option label="希伯来语" value="he" />
            <el-option label="印地语" value="hi" />
            <el-option label="匈牙利语" value="hu" />
            <el-option label="印尼语" value="id" />
            <el-option label="意大利语" value="it" />
            <el-option label="日语" value="ja" />
            <el-option label="卡纳达语" value="kn" />
            <el-option label="韩语" value="ko" />
            <el-option label="马拉雅拉姆语" value="ml" />
            <el-option label="马拉地语" value="mr" />
            <el-option label="马来语" value="ms" />
            <el-option label="荷兰语" value="nl" />
            <el-option label="奥里亚语" value="or" />
            <el-option label="旁遮普语" value="pa" />
            <el-option label="波兰语" value="pl" />
            <el-option label="葡萄牙语" value="pt" />
            <el-option label="拉贾斯坦语" value="raj" />
            <el-option label="罗马尼亚语" value="ro" />
            <el-option label="俄语" value="ru" />
            <el-option label="瑞典语" value="sv" />
            <el-option label="泰米尔语" value="ta" />
            <el-option label="泰卢固语" value="te" />
            <el-option label="泰语" value="th" />
            <el-option label="土耳其语" value="tr" />
            <el-option label="乌克兰语" value="uk" />
            <el-option label="越南语" value="vi" />
            <el-option label="简体中文" value="zh" />
            <el-option label="繁体中文" value="zh-Hant" />
          </el-select>
        </el-form-item>

        <!-- 性别定向 -->
        <el-form-item label="性别定向">
          <el-select
              v-model="targetingSpec.gender"
              placeholder="请选择性别定向"
              style="width: 100%"
          >
            <el-option label="不限" value="GENDER_UNLIMITED" />
            <el-option label="男性" value="GENDER_MALE" />
            <el-option label="女性" value="GENDER_FEMALE" />
          </el-select>
        </el-form-item>
      </el-form>

      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="handleSubmit">确定</el-button>
      </span>
    </el-dialog>
    <!-- 预算调整弹窗 -->
    <el-dialog
        title="调整预算"
        :visible.sync="budgetDialogVisible"
        width="450px"
    >
      <el-form label-width="100px">
        <el-form-item label="广告组">
          <span>{{ currentAdgroup.adgroup_name }}</span>
        </el-form-item>
        <el-form-item label="预算模式">
          <span>{{ getBudgetModeLabel(currentAdgroup.budget_mode) }}</span>
        </el-form-item>
        <el-form-item label="当前预算" v-if="currentAdgroup.budget_mode !== 'BUDGET_MODE_INFINITE'">
          <span>¥{{ formatBudget(currentAdgroup.budget) }}</span>
        </el-form-item>
        <el-form-item label="新预算" prop="new_budget" v-if="currentAdgroup.budget_mode !== 'BUDGET_MODE_INFINITE'">
          <budget-input v-model="newBudget" :min-budget="5000" />
        </el-form-item>
        <el-form-item v-else label="提示">
          <span class="tip-text">无限预算模式无需设置预算</span>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="budgetDialogVisible = false">取消</el-button>
        <el-button
            type="primary"
            :loading="budgetLoading"
            @click="handleBudgetSubmit"
            v-if="currentAdgroup.budget_mode !== 'BUDGET_MODE_INFINITE'"
        >
          确定
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { adgroupApi } from '@/api/adgroup'
import { campaignApi } from '@/api/campaign'
import StatusTag from '@/components/StatusTag'
import BudgetInput from '@/components/BudgetInput'
import FieldSelector from '@/components/FieldSelector'
import {
  OPTIMIZATION_GOAL,
  OPTIMIZATION_GOAL_LABEL,
  BUDGET_MODE_LABEL,
  BID_TYPE_LABEL,
  PLACEMENT_LABEL
} from '@/utils/constants'
import { ADGROUP_FIELDS, ADGROUP_FIELD_LABEL } from '@/utils/adgroupFieldConstants'
import {appApi as catalogApi} from "@/api/app";

export default {
  name: 'AdgroupList',
  components: {
    StatusTag,
    BudgetInput,
    FieldSelector
  },
  data() {
    return {
      adgroups: [],
      loading: false,
      tableKey: 0,
      campaignOptions: [],

      // 分页
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0
      },

      // 筛选条件
      filter: {
        adgroup_name: '',
        campaign_id: '',
        optimization_goal: '',
        promotion_type: '',
        operation_status: ''
      },

      // 路由参数
      routeQueryCampaignId: this.$route.query.campaignId || '',
      routeQueryCampaignName: this.$route.query.campaignName || '',

      // 字段选择相关
      allFields: [],
      selectedFields: [],
      defaultFields: [],
      STORAGE_KEY: 'adgroup_selected_fields',

      // 选项数据
      optimizationGoalOptions: [

        { value: OPTIMIZATION_GOAL.VALUE, label: '价值' },
      ],

      locationOptions: [],
      locationLoading: false,
      audienceOptions: [],
      // 小游戏/小程序相关
      minisOptions: [],
      minisLoading: false,
      // 创建/编辑弹窗
      dialogVisible: false,
      dialogTitle: '创建广告组',
      submitLoading: false,
      isEdit: false,
      editAdgroupId: null,
      formData: {

        minis_id: '',
        adgroup_name: '',
        campaign_id: '',
        promotion_type: 'MINI_GAME',
        optimization_goal: 'VALUE',
        optimization_event: 'AD_REVENUE_VALUE',
        bid_type: 'BID_TYPE_NO_BID',
        conversion_bid_price: null,
        bid_price:null,
        deep_bid_type: '',
        roas_bid: null,
        schedule_type: 'SCHEDULE_FROM_NOW',
        schedule_start_time: null,
        schedule_end_time: null,
        placement_type: 'PLACEMENT_TYPE_AUTOMATIC',
        placements: ['PLACEMENT_TIKTOK']
      },

      targetingSpec: {
        location_ids: [],
        languages:['en'],
        gender: 'GENDER_UNLIMITED'
      },

      formRules: {
        adgroup_name: [
          { required: true, message: '请输入广告组名称', trigger: 'blur' },
          { max: 100, message: '名称不能超过100个字符', trigger: 'blur' }
        ],
        campaign_id: [
          { required: true, message: '请选择所属系列', trigger: 'change' }
        ],
        schedule_start_time: [
          { required: true, message: '请选择开始时间', trigger: 'change' }
        ],
        minis_id: [
          { required: true, message: '请选择小游戏/小程序', trigger: 'change' }
        ],
      },

      startTimeOptions: {
        disabledDate(time) {
          return time.getTime() < Date.now() - 24 * 60 * 60 * 1000
        }
      },
      endTimeOptions: {
        disabledDate(time) {
          return time.getTime() < Date.now()
        }
      },

      // 预算调整弹窗
      budgetDialogVisible: false,
      budgetLoading: false,
      currentAdgroup: {},
      newBudget: null
    }
  },
  computed: {
    displayFields() {
      const fields = []
      for (const key of this.selectedFields) {
        const fieldConfig = this.allFields.find(f => f.key === key)
        if (fieldConfig) {
          fields.push(fieldConfig)
        }
      }
      return fields
    }
  },
  mounted() {
    this.initFields()
    this.loadSelectedFields()
    this.fetchCampaignOptions()
    this.fetchAds()
  },
  methods: {
    /**
     * 格式化日期时间为 YYYY-MM-DD HH:mm:ss 格式（UTC+0）
     */
    formatDateTime(dateStr) {
      if (!dateStr) return null

      const date = new Date(dateStr)

      // 获取UTC时间的各个部分
      const year = date.getUTCFullYear()
      const month = String(date.getUTCMonth() + 1).padStart(2, '0')
      const day = String(date.getUTCDate()).padStart(2, '0')
      const hours = String(date.getUTCHours()).padStart(2, '0')
      const minutes = String(date.getUTCMinutes()).padStart(2, '0')
      const seconds = String(date.getUTCSeconds()).padStart(2, '0')

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    /**
     * 加载小游戏/小程序列表
     */
    async loadMinisOptions() {
      this.minisLoading = true
      try {
        const advertiserId = '7598883753192357896'
        const res = await catalogApi.getMinisList({advertiserId})

        if (res.code === 0) {
          this.minisOptions = res.data.list || res.data || []
        } else {
          this.$message.error(res.message || '获取小游戏/小程序列表失败')
        }
      } catch (error) {
        console.error('加载小游戏/小程序列表失败', error)
        this.$message.error('加载小游戏/小程序列表失败')
      } finally {
        this.minisLoading = false
      }

    },
    /**
     * 初始化字段配置
     */
    initFields() {
      this.allFields = ADGROUP_FIELDS.map(field => ({
        ...field,
        label: ADGROUP_FIELD_LABEL[field.key] || field.label,
        width: this.getFieldWidth(field.key),
        minWidth: this.getFieldMinWidth(field.key)
      }))
      this.defaultFields = this.allFields.filter(f => f.default).map(f => f.key)
    },

    getFieldWidth(key) {
      const widthMap = {
        adgroup_id: 180,
        adgroup_name: 200,
        campaign_name: 180,
        optimization_goal: 120,
        budget: 150,
        bid_type: 120,
        conversion_bid_price: 120,
        promotion_type: 120,
        placements: 180,
        operation_status: 100,
        schedule_start_time: 160,
        schedule_end_time: 160,
        create_time: 160,
        modify_time: 160,
        targeting_summary: 200
      }
      return widthMap[key] || null
    },

    getFieldMinWidth(key) {
      const minWidthMap = {
        adgroup_name: 180,
        placements: 150
      }
      return minWidthMap[key] || null
    },

    loadSelectedFields() {
      const saved = localStorage.getItem(this.STORAGE_KEY)
      if (saved) {
        try {
          const parsed = JSON.parse(saved)
          const validFields = parsed.filter(key => this.allFields.some(f => f.key === key))
          this.selectedFields = [...new Set([...this.defaultFields, ...validFields])]
        } catch (e) {
          this.selectedFields = [...this.defaultFields]
        }
      } else {
        this.selectedFields = [...this.defaultFields]
      }
    },

    saveSelectedFields() {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.selectedFields))
    },

    handleFieldsConfirm(selectedFields) {
      this.selectedFields = selectedFields
      this.saveSelectedFields()
      this.tableKey++
    },

    formatFieldValue(value) {
      if (value === null || value === undefined) return '-'
      if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '-'
      if (typeof value === 'object') return JSON.stringify(value)
      if (typeof value === 'boolean') return value ? '是' : '否'
      return String(value)
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$message.success('已复制到剪贴板')
      }).catch(() => {
        this.$message.error('复制失败')
      })
    },

    /**
     * 获取定向摘要
     */
    getTargetingSummary(row) {
      if (!row.targeting_spec) return '-'
      const summary = []
      if (row.targeting_spec.location_ids?.length) {
        summary.push(`${row.targeting_spec.location_ids.length}个地区`)
      }
      if (row.targeting_spec.age_groups?.length) {
        summary.push(`${row.targeting_spec.age_groups.length}个年龄段`)
      }
      if (row.targeting_spec.gender && row.targeting_spec.gender !== 'GENDER_UNLIMITED') {
        summary.push(row.targeting_spec.gender === 'GENDER_MALE' ? '男性' : '女性')
      }
      return summary.length ? summary.join('、') : '不限'
    },

    /**
     * 获取系列列表
     */
    async fetchCampaignOptions() {
      try {
        const advertiserId = '7598883753192357896'
        const res = await campaignApi.getList({
          advertiserId,
          page: 1,
          pageSize: 100
        })
        if (res.code === 0) {
          this.campaignOptions = res.data.list || []
        }
      } catch (error) {
        console.error('获取系列列表失败', error)
      }
    },

    /**
     * 获取广告组列表
     */
    async fetchAds() {
      this.loading = true
      try {
        const advertiserId ='7598883753192357896'

        const params = {
          advertiserId,
          page: this.pagination.page,
          pageSize: this.pagination.pageSize,
          adgroupName: this.filter.adgroup_name,
          campaignId: this.filter.campaign_id || this.routeQueryCampaignId,
          optimizationGoal: this.filter.optimization_goal,
          promotionType: this.filter.promotion_type,
          operationStatus: this.filter.operation_status
        }

        const res = await adgroupApi.getList(params)

        if (res.code === 0) {
          this.adgroups = res.data.list || []
          this.pagination.total = res.data.pageInfo?.totalNumber || 0
        }
      } catch (error) {
        console.error('获取列表失败', error)
        this.$message.error(error.message || '获取列表失败')
      } finally {
        this.loading = false
      }
    },

    handleCampaignChange() {
      this.pagination.page = 1
      this.fetchAds()
    },

    handleSearch() {
      this.pagination.page = 1
      this.fetchAds()
    },

    handleReset() {
      this.filter = {
        adgroup_name: '',
        campaign_id: '',
        optimization_goal: '',
        promotion_type: '',
        operation_status: ''
      }
      this.pagination.page = 1
      this.fetchAds()
    },

    handlePageChange(page) {
      this.pagination.page = page
      this.fetchAds()
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.fetchAds()
    },

    /**
     * 加载地理位置选项
     */
    async loadLocationOptions() {
      this.locationLoading = true
      try {

        // 模拟数据 - 国家级地域定向ID
        this.locationOptions = [
          { id: '6252001', name: '美国 (US)' },
          { id: '3469034', name: '巴西 (BR)' },
          { id: '1861060', name: '日本 (JP)' },
          { id: '2635167', name: '英国 (GB)' },
        ]
      } catch (error) {
        console.error('加载地理位置失败', error)
      } finally {
        this.locationLoading = false
      }
    },



    /**
     * 处理投放策略切换
     */
    handlePlacementTypeChange() {
      if (this.formData.placement_type === 'PLACEMENT_TYPE_AUTOMATIC') {
        this.formData.placements = []
      } else {
        this.formData.placements = ['PLACEMENT_TIKTOK']
      }
    },

    /**
     * 创建广告组
     */
    handleCreate() {
      this.loadLocationOptions()
      this.isEdit = false
      this.dialogTitle = '创建广告组'

      // 获取当前时间的 UTC+0 格式
      const now = new Date()
      const scheduleStartTime = this.formatDateTime(now)

      this.formData = {
        adgroup_name: '',
        campaign_id: this.routeQueryCampaignId || '',
        promotion_type: 'MINI_GAME',
        optimization_goal: 'VALUE',
        optimization_event: 'AD_REVENUE_VALUE',
        bid_type: 'BID_TYPE_NO_BID',
        conversion_bid_price: null,
        bid_price:null,
        deep_bid_type: '',
        roas_bid: null,
        schedule_type: 'SCHEDULE_FROM_NOW',
        schedule_start_time: scheduleStartTime,
        schedule_end_time: null,
        placement_type: 'PLACEMENT_TYPE_AUTOMATIC',
        placements: ['PLACEMENT_TIKTOK']
      }
      this.targetingSpec = {
        location_ids: ["6252001"],
        languages: ['en']
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.adgroupForm && this.$refs.adgroupForm.clearValidate()
      })
    },

    /**
     * 编辑广告组
     */
    handleEdit(row) {
      this.isEdit = true
      this.dialogTitle = '编辑广告组'
      this.editAdgroupId = row.adgroup_id
      this.formData = {
        adgroup_name: row.adgroup_name,
        campaign_id: row.campaign_id,
        promotion_type: row.promotion_type || 'MINI_GAME',
        optimization_goal: row.optimization_goal || 'VALUE',
        optimization_event: row.optimization_event || 'AD_REVENUE_VALUE',
        bid_type: row.bid_type || 'BID_TYPE_NO_BID',
        conversion_bid_price: row.conversion_bid_price || null,
        bid_price:row.bid_price ||null,
        deep_bid_type: row.deep_bid_type || '',
        roas_bid: row.roas_bid || null,
        schedule_type: row.schedule_type || 'SCHEDULE_FROM_NOW',
        schedule_start_time: row.schedule_start_time,
        schedule_end_time: row.schedule_end_time,
        placement_type: row.placement_type || 'PLACEMENT_TYPE_AUTOMATIC',
        placements: row.placements || ['PLACEMENT_TIKTOK']
      }

      if (row.targeting_spec) {
        this.targetingSpec = {
          location_ids: row.targeting_spec.location_ids || [],
          languages: row.targeting_spec.languages || [],
          gender: row.targeting_spec.gender || 'GENDER_UNLIMITED'
        }
      }

      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.adgroupForm && this.$refs.adgroupForm.clearValidate()
      })
    },
    /**
     * 提交表单
     */
    async handleSubmit() {
      try {
        await this.$refs.adgroupForm.validate()

        // 验证目标出价
        if (this.formData.bid_type === 'BID_TYPE_CUSTOM' && !this.formData.conversion_bid_price) {
          this.$message.warning('请设置目标出价')
          return
        }

        this.submitLoading = true



        // 根据后端 AdgroupCreateRequest 构建请求数据
        const requestData = {
          advertiser_id:"7598883753192357896",
          campaign_id: this.formData.campaign_id,
          adgroup_name: this.formData.adgroup_name,
          minis_id: this.formData.minis_id,
          vbo_window:'ZERO_DAY',
          promotion_type: this.formData.promotion_type || 'MINI_GAME',
          optimization_goal: this.formData.optimization_goal || 'VALUE',
          optimization_event: this.formData.optimization_event || 'AD_REVENUE_VALUE',
          bid_type: this.formData.bid_type,
          billing_event: 'OCPM', // 默认计费事件
          schedule_type: this.formData.schedule_type,
          schedule_start_time: this.formatDateTime(this.formData.schedule_start_time),
          placement_type: this.formData.placement_type,
          placements: this.formData.placement_type === 'PLACEMENT_TYPE_AUTOMATIC'
            ? ['PLACEMENT_TIKTOK']
            : (this.formData.placements || ['PLACEMENT_TIKTOK']),
          targeting_spec: {
            location_ids: this.targetingSpec.location_ids || [],
            languages: this.targetingSpec.languages || []
          }
        }

        // 添加性别定向
        if (this.targetingSpec.gender && this.targetingSpec.gender !== 'GENDER_UNLIMITED') {
          requestData.targeting_spec.gender = this.targetingSpec.gender
        }

        // 手动出价时添加目标出价
        if (this.formData.bid_type === 'BID_TYPE_CUSTOM') {
          requestData.conversion_bid_price = this.formData.conversion_bid_price
          requestData.bid_price = this.formData.bid_price
        }

        // 价值优化参数
        if (this.formData.optimization_goal === 'VALUE') {
          if (this.formData.deep_bid_type) {
            requestData.deep_bid_type = this.formData.deep_bid_type
          }
          if (this.formData.roas_bid) {
            requestData.roas_bid = this.formData.roas_bid
          }
        }

        // 终身预算时需要结束时间
        if (this.formData.schedule_type === 'SCHEDULE_START_END') {
          requestData.schedule_end_time = this.formData.schedule_end_time
        }

        // 非编辑状态添加request_id
        if (!this.isEdit) {
          requestData.request_id = Date.now().toString()
        } else {
          // 编辑状态添加adgroup_id
          requestData.adgroup_id = this.editAdgroupId
        }

        let res
        if (this.isEdit) {
          res = await adgroupApi.update(requestData)
        } else {
          res = await adgroupApi.create(requestData)
        }

        if (res.code === 0) {
          this.$message.success(this.isEdit ? '编辑成功' : '创建成功')
          this.dialogVisible = false
          await this.fetchAds()
        } else {
          this.$message.error(res.message || '操作失败')
        }
      } catch (err) {
        if (err !== 'cancel') {
          this.$message.error(err.message || '操作失败')
        }
      } finally {
        this.submitLoading = false
      }
    },

    /**
     * 预算调整弹窗
     */
    handleBudgetEdit(row) {
      this.currentAdgroup = row
      this.newBudget = row.budget
      this.budgetDialogVisible = true
    },

    async handleBudgetSubmit() {
      if (!this.newBudget || this.newBudget < 5000) {
        this.$message.warning('预算至少为5000分（¥50）')
        return
      }

      this.budgetLoading = true
      try {
        const advertiserId = localStorage.getItem('advertiser_id')
        const res = await adgroupApi.updateBudget({
          advertiserId,
          budget: [{
            adgroupId: this.currentAdgroup.adgroup_id,
            budget: this.newBudget
          }]
        })

        if (res.code === 0) {
          this.$message.success('预算更新成功')
          this.budgetDialogVisible = false
          await this.fetchAds()
        } else {
          this.$message.error(res.message || '更新失败')
        }
      } catch (error) {
        this.$message.error('更新失败')
      } finally {
        this.budgetLoading = false
      }
    },

    /**
     * 暂停广告组
     */
    handlePause(row) {
      this.$confirm('确定要暂停该广告组吗？暂停后广告将停止投放。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const advertiserId = localStorage.getItem('advertiser_id')
          const res = await adgroupApi.updateStatus({
            advertiserId,
            adgroupIds: [row.adgroup_id],
            operation_status: 'DISABLE'
          })
          if (res.code === 0) {
            this.$message.success('已暂停')
            await this.fetchAds()
          }
        } catch (error) {
          this.$message.error('操作失败')
        }
      }).catch(() => {})
    },

    /**
     * 启用广告组
     */
    handleResume(row) {
      this.$confirm('确定要启用该广告组吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'info'
      }).then(async () => {
        try {
          const advertiserId = localStorage.getItem('advertiser_id')
          const res = await adgroupApi.updateStatus({
            advertiserId,
            adgroupIds: [row.adgroup_id],
            operation_status: 'ENABLE'
          })
          if (res.code === 0) {
            this.$message.success('已启用')
            await this.fetchAds()
          }
        } catch (error) {
          this.$message.error('操作失败')
        }
      }).catch(() => {})
    },

    /**
     * 删除广告组
     */
    handleDelete(row) {
      this.$confirm('删除后无法恢复，该广告组下的所有广告也将被删除。确定要删除吗？', '警告', {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'error'
      }).then(async () => {
        try {
          const advertiserId = localStorage.getItem('advertiser_id')
          const res = await adgroupApi.updateStatus({
            advertiserId,
            adgroupIds: [row.adgroup_id],
            operation_status: 'DELETE'
          })
          if (res.code === 0) {
            this.$message.success('已删除')
            await this.fetchAds()
          }
        } catch (error) {
          this.$message.error('操作失败')
        }
      }).catch(() => {})
    },

    /**
     * 查看广告
     */
    handleViewAds(row) {
      this.$router.push({
        path: '/ads',
        query: {
          adgroupId: row.adgroup_id,
          adgroupName: row.adgroup_name,
          campaignId: row.campaign_id
        }
      })
    },

    handleDialogClose() {
      this.formData = {}
      this.editAdgroupId = null
      this.targetingSpec = {
        location_ids: [],
        languages: ['en']
      }
    },

    /**
     * 格式化方法
     */
    formatBudget(budget) {
      if (budget === undefined || budget === null) return '-'
      return (budget / 100).toFixed(2)
    },

    formatTime(timeStr) {
      if (!timeStr) return '-'
      return timeStr.replace(' ', '\n')
    },

    getOptimizationGoalLabel(goal) {
      return OPTIMIZATION_GOAL_LABEL[goal] || goal || '-'
    },

    getBudgetModeLabel(mode) {
      return BUDGET_MODE_LABEL[mode] || mode || '-'
    },

    getBidTypeLabel(type) {
      return BID_TYPE_LABEL[type] || type || '-'
    },

    getPlacementLabel(placement) {
      return PLACEMENT_LABEL[placement] || placement
    },

    getPromotionTypeLabel(type) {
      const labelMap = {
        APP_ANDROID: 'Android应用',
        APP_IOS: 'iOS应用',
        WEBSITE: '网站',
        LEAD_GENERATION: '销售线索',
        MINI_APP: '小程序',
        MINI_GAME: '小游戏'
      }
      return labelMap[type] || type || '-'
    }
  },
  filters: {
    truncate(str, len) {
      if (!str) return ''
      return str.length > len ? str.substring(0, len) + '...' : str
    }
  }
}
</script>

<style scoped>
.adgroup-list {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  color: #1f2f3d;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-card {
  margin-bottom: 16px;
}

.id-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.id-text {
  font-family: monospace;
  font-size: 12px;
  color: #909399;
}

.copy-icon {
  cursor: pointer;
  color: #909399;
  font-size: 14px;
  opacity: 0;
  transition: opacity 0.2s;
}

.id-cell:hover .copy-icon {
  opacity: 1;
}

.copy-icon:hover {
  color: #409eff;
}

.adgroup-name {
  font-weight: 500;
  color: #303133;
}

.budget-mode {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.bid-price {
  font-size: 11px;
  color: #909399;
  margin-left: 4px;
}

.time-text {
  font-size: 12px;
  line-height: 1.4;
  white-space: pre-line;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-left: 8px;
}

.tip-text {
  font-size: 12px;
  color: #e6a23c;
}

.location-selector {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.targeting-summary {
  font-size: 12px;
  color: #606266;
}
</style>